import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useIsMobile } from "../../hooks/useMobile";
import { NEXT_7_DAYS_VALUE, resolveWeekStartDaySelection, useWeekStartDayOptions } from "../../hooks/useWeekStartDayOptions";
import { NEUTRAL } from "../../theme/tokens";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown/SystemDropdown";

// Per-surface analytics event + testid prefix so the shared selector stays attributable to where it renders.
const SOURCE_CONFIG = {
	profile: { event: "profile_week_start_changed", testIdPrefix: "profile" },
	staffSchedule: { event: "staff_schedule_week_start_changed", testIdPrefix: "staff-schedule" },
} as const;

type WeekStartDaySource = keyof typeof SOURCE_CONFIG;

// Mobile: strip the outlined-field chrome down to a compact, tinted pill that reads as a tappable
// control rather than a stray form field. Spacing/shape come from the theme (numeric `py`/`pl` are
// theme.spacing units, `borderRadius: 1` is theme.shape.borderRadius) so the pill tracks global
// density/shape. Right padding is left to MUI's default icon spacing. `&&` raises specificity so it
// beats SystemDropdown's own hover/focus border rules. Desktop keeps the standard dropdown.
const MOBILE_SELECT_SX: SxProps<Theme> = {
	"& .MuiSelect-select": { py: 0.5, pl: 1.25, borderRadius: 1, backgroundColor: NEUTRAL[100], color: "primary.main", fontWeight: 600 },
	"& .MuiSelect-icon": { color: "primary.main" },
	"& .MuiOutlinedInput-notchedOutline": { border: 0 },
	"&&:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
	"&&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
};

/**
 * Week-start day selector shared by the Profile weekly schedule and the mobile staff schedule.
 * Reads/writes the SAME session `viewStartDay`/`setViewStartDay` the Staffing grid mutates, so a
 * user's choice stays consistent across surfaces. Render only when FEATURE_FLAGS.StaffingDynamicWeekStart
 * is on (gated by the caller).
 */
export const WeekStartDaySelector: FC<{ source: WeekStartDaySource }> = ({ source }) => {
	const { t } = useTranslation();
	const { viewStartDay, setViewStartDay } = useWeekIndex();
	const { chosenDepartment } = useChosenDepartment();
	const { track } = useAnalytics();
	const { isMobile } = useIsMobile();

	const weekStartDayOptions = useWeekStartDayOptions();
	const { event, testIdPrefix } = SOURCE_CONFIG[source];

	const onChange = (value: number | undefined): void => {
		if (value === undefined || value === viewStartDay) return;
		setViewStartDay(resolveWeekStartDaySelection(value, chosenDepartment.timezone));
		track(event, {
			week_start_day: value === NEXT_7_DAYS_VALUE ? "next_7_days" : value,
			department_default: chosenDepartment.week_start_day,
		});
	};

	return (
		<Box data-testid={`${testIdPrefix}-week-start-selector`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<SystemDropdown
				items={weekStartDayOptions}
				selectedItem={viewStartDay}
				onChange={onChange}
				label={isMobile ? "" : t("schedule.template.week_start")}
				formControlSx={{ minWidth: isMobile ? "auto" : "140px" }}
				selectSx={isMobile ? MOBILE_SELECT_SX : undefined}
				data-testid={`${testIdPrefix}-week-start-dropdown`}
			/>
		</Box>
	);
};
